/**
 * Kanban API — thin typed wrappers over the gateway, mirroring
 * apps/chaos-gateway/src/routes/kanban/*.
 *
 * Every method returns a parsed result (consumers don't need to JSON-
 * parse manually). On non-2xx the underlying `apiJson` throws an
 * `ApiError` which TanStack Query surfaces.
 */
import type {
  Board,
  Card,
  Column,
  CreateBoardInput,
  CreateCardInput,
  CreateColumnInput,
  MoveCardInput,
  UpdateBoardInput,
  UpdateCardInput,
  UpdateColumnInput,
} from '@chaos/types/kanban';

import { apiJson } from './client';

interface BoardsListResponse {
  boards: Board[];
}
interface BoardResponse {
  board: Board;
}
interface ColumnsListResponse {
  columns: Column[];
}
interface ColumnResponse {
  column: Column;
}
interface CardsListResponse {
  cards: Card[];
}
interface CardResponse {
  card: Card;
}

export const kanbanApi = {
  // ── Boards ────────────────────────────────────────────────────────
  listBoards(): Promise<Board[]> {
    return apiJson<BoardsListResponse>('/kanban/boards').then((r) => r.boards);
  },
  getBoard(boardId: string): Promise<Board> {
    return apiJson<BoardResponse>(`/kanban/boards/${boardId}`).then((r) => r.board);
  },
  createBoard(input: CreateBoardInput): Promise<Board> {
    return apiJson<BoardResponse>('/kanban/boards', { method: 'POST', json: input }).then(
      (r) => r.board,
    );
  },
  updateBoard(boardId: string, input: UpdateBoardInput): Promise<Board> {
    return apiJson<BoardResponse>(`/kanban/boards/${boardId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.board);
  },
  deleteBoard(boardId: string): Promise<Board> {
    return apiJson<BoardResponse>(`/kanban/boards/${boardId}`, { method: 'DELETE' }).then(
      (r) => r.board,
    );
  },

  // ── Columns ───────────────────────────────────────────────────────
  listColumns(boardId: string): Promise<Column[]> {
    return apiJson<ColumnsListResponse>(`/kanban/boards/${boardId}/columns`).then((r) => r.columns);
  },
  createColumn(boardId: string, input: CreateColumnInput): Promise<Column> {
    return apiJson<ColumnResponse>(`/kanban/boards/${boardId}/columns`, {
      method: 'POST',
      json: input,
    }).then((r) => r.column);
  },
  updateColumn(columnId: string, input: UpdateColumnInput): Promise<Column> {
    return apiJson<ColumnResponse>(`/kanban/columns/${columnId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.column);
  },
  deleteColumn(columnId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/kanban/columns/${columnId}`, { method: 'DELETE' });
  },

  // ── Cards ─────────────────────────────────────────────────────────
  listCards(boardId: string): Promise<Card[]> {
    return apiJson<CardsListResponse>(`/kanban/boards/${boardId}/cards`).then((r) => r.cards);
  },
  getCard(cardId: string): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}`).then((r) => r.card);
  },
  createCard(input: CreateCardInput): Promise<Card> {
    return apiJson<CardResponse>('/kanban/cards', { method: 'POST', json: input }).then(
      (r) => r.card,
    );
  },
  updateCard(cardId: string, input: UpdateCardInput): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.card);
  },
  moveCard(cardId: string, input: MoveCardInput): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}/move`, {
      method: 'POST',
      json: input,
    }).then((r) => r.card);
  },
  deleteCard(cardId: string): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}`, { method: 'DELETE' }).then(
      (r) => r.card,
    );
  },
  // Reverses an archive — used by the "Undo" toast that fires
  // immediately after deleteCard. The gateway endpoint bypasses the
  // `archivedAt IS NULL` filter the other write routes apply.
  restoreCard(cardId: string): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}/restore`, { method: 'POST' }).then(
      (r) => r.card,
    );
  },
};

// ── Query-key factory ───────────────────────────────────────────────
// Centralised so cache invalidations and prefetches use the same keys.
export const kanbanKeys = {
  all: ['kanban'] as const,
  boards: () => [...kanbanKeys.all, 'boards'] as const,
  board: (boardId: string) => [...kanbanKeys.all, 'board', boardId] as const,
  boardColumns: (boardId: string) => [...kanbanKeys.all, 'board', boardId, 'columns'] as const,
  boardCards: (boardId: string) => [...kanbanKeys.all, 'board', boardId, 'cards'] as const,
  card: (cardId: string) => [...kanbanKeys.all, 'card', cardId] as const,
};
